/**
 * The identity section at the top of the configure step: which medication this
 * actually is, as opposed to how it is taken.
 *
 * The patient types the product, and each field below then offers only the
 * variations that product actually has where she lives — dose form, strength,
 * container, pack size. She is never asked to choose a pack out of a list of
 * candidates; the combination she ends up with is what identifies it, and the
 * server does the identifying. When she scanned the box on the way in, these
 * fields arrive already filled.
 *
 * Nothing she typed is discarded when the catalog has no match for it, and the
 * country is never asked for — the app already knows which market she is in.
 */
import React, { useCallback, useEffect, useMemo } from "react";
import { Text, View } from "react-native";
import { Check } from "lucide-react-native";

import {
  InlineOptionPicker,
  TextActionButton,
  MoonFieldInput,
  MoonFormField,
  MoonSearchInput,
  Row,
  Tag,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  isFilledFromScan,
  matchTypedProduct,
  useMedicationProductVariations,
  withProductName,
  type MedicationCatalogProduct,
  type MedicationIdentityDraft,
} from "@src/packages/medications";

import { EMBER } from "../../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";

type OptionSectionProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (next: string) => void;
  testID: string;
  /** Whether a pack exists with this option beside the choices above it. */
  isAvailable?: (option: string) => boolean;
};

/**
 * One identity field, offered as the variations this product actually has.
 *
 * There is deliberately no text input here, and no "something else". A field
 * that is sometimes a chip list and sometimes a free-text box is two different
 * questions wearing one label, and the typed half cannot be matched to a pack
 * anyway. When the catalog has nothing to offer for a product, the section
 * simply does not appear — the product name she typed is kept as it is.
 */
function IdentityOptionSection({
  label,
  value,
  options,
  onChange,
  testID,
  isAvailable,
}: OptionSectionProps) {
  // A value that arrived from a scan is always shown, even in the unlikely
  // case that the product lookup does not list it, so the screen never
  // contradicts the box she just scanned. It goes last: the catalog returns
  // these in its own order — strengths ascending — and a stray value pinned to
  // the front would break that for every other option.
  const choices = useMemo(
    () => (value && !options.includes(value) ? [...options, value] : options),
    [options, value],
  );
  // Nothing to ask: no options at all, or exactly one, which the summary
  // line above has already stated. Rendering it here too said the same thing
  // twice, once as a fact and once as a choice she appeared to have made.
  if (choices.length <= 1) return null;

  return (
    <MoonFormField label={label} style={{ marginBottom: 16 }}>
      <InlineOptionPicker
        options={choices.map((option) => ({
          id: option,
          label: option.charAt(0).toUpperCase() + option.slice(1),
          action: testID,
          testID: `${testID}-option`,
          // Shown, but not choosable: no pack has it beside what she has
          // already settled further up.
          disabled: isAvailable ? !isAvailable(option) : false,
        }))}
        value={value}
        // The only option the catalogue has is not a choice, so it does not
        // toggle: tapping it again would clear the field and leave the pack
        // described by nothing. Where there are alternatives a tap still
        // clears, so a field she is unsure of can be left blank.
        onChange={(next) =>
          onChange(choices.length === 1 || value !== next ? next : "")
        }
        wrap
      />
    </MoonFormField>
  );
}

type Props = {
  value: MedicationIdentityDraft;
  onChange: (next: MedicationIdentityDraft) => void;
  /**
   * The product she settled on in the search step, carried here rather than
   * looked up again. The search already knows which catalogue row she picked;
   * re-deriving it from the name she sees is how a product the search had just
   * offered could come back as "no pack on file".
   */
  product?: MedicationCatalogProduct | null;
  productSelected?: boolean;
  productLeading?: React.ReactNode;
  /** Renders the illustration for a pack, as her choices narrow it. */
  renderPackLeading?: (pack: {
    doseForm: string;
    container: string;
  }) => React.ReactNode;
  /** Takes her back to the search step to pick a different medication. */
  onChangeProduct?: () => void;
  /** The routes the chosen packs are given by, for the course section. */
  onRoutesChange?: (routes: string[]) => void;
};

const PACK_FIELDS = [
  "doseForm",
  "strength",
  "container",
  "packQuantity",
] as const;
type PackField = (typeof PACK_FIELDS)[number];

export function MedicationIdentitySection({
  value,
  onChange,
  product = null,
  productSelected = false,
  productLeading,
  renderPackLeading,
  onChangeProduct,
  onRoutesChange,
}: Props) {
  // Derived, not remembered: a flag set when the scan resolved would still be
  // true after she picked a different product, and the screen would claim the
  // fields came from a box they do not describe.
  const filledFromScan = isFilledFromScan(value);
  const t = useTranslations();

  // Only ever a fallback: a scanned pack, an OCR'd prescription and an edit
  // all reach this step without a product having been picked, and the name is
  // then the only handle there is.
  const { data: products = [], isFetching: isSearchingProducts } =
    useMedicationProductVariations(
      product ? "" : value.productName,
      value.countryCode,
    );

  const matchedProduct = useMemo(
    () => product ?? matchTypedProduct(value.productName, products),
    [product, value.productName, products],
  );

  const suggestions = useMemo(
    () => (matchedProduct ? [] : products.slice(0, 6)),
    [matchedProduct, products],
  );

  /**
   * The options still open to her, pack by pack.
   *
   * `dimensions` is the union of everything the product comes in, so offering
   * it whole lets her build a combination no pack has — a 900 UI in a flacon
   * when 900 UI only exists in a cartouche — and the server then cannot
   * resolve what she described. The packs are the truth: each field offers
   * only the values that survive the choices she has already made, so every
   * combination reachable on screen is one that exists.
   */
  const optionsFor = useCallback(
    (field: PackField) => {
      const packs = matchedProduct?.packs ?? [];
      return Array.from(
        new Set(
          packs
            .map((pack) => pack[field])
            .filter((option): option is string => !!option),
        ),
      );
    },
    [matchedProduct],
  );

  /**
   * Choosing a value drops the other choices no pack can keep beside it.
   *
   * Narrowing the lists instead was worse: it left fields with one option,
   * which cannot be deselected, so she could not change her mind about
   * anything once she had chosen. Every value the product has stays on
   * screen; what gives way is whatever else she picked that no pack agrees
   * with, and whatever is left with one answer settles itself.
   */
  const choosePackField = useCallback(
    (field: PackField, next: string) => {
      // The fields read down the pack: a dose form narrows the strengths, a
      // strength narrows the containers, and so on. Changing one therefore
      // only unsettles what comes after it — what she chose above still
      // holds, and clearing it would throw away an answer she never changed.
      const settled: Record<PackField, string> = {
        doseForm: value.doseForm,
        strength: value.strength,
        container: value.container,
        packQuantity: value.packQuantity,
        [field]: next,
      };
      const from = PACK_FIELDS.indexOf(field);
      for (const downstream of PACK_FIELDS.slice(from + 1)) {
        settled[downstream] = "";
      }
      onChange({ ...value, ...settled });
    },
    [onChange, value],
  );

  /**
   * Whether a pack exists with this option beside everything chosen above it.
   *
   * Only the fields upstream constrain it. An option that no pack can hold is
   * shown and disabled rather than hidden: she can see the product does come
   * in 900 UI, and that it is not reachable from the dose form she picked.
   */
  /**
   * The routes the packs she has narrowed to are given by.
   *
   * A route belongs to the dose form rather than to the box, and a product
   * can allow several from the same pack — Utrogestan is swallowed or used
   * vaginally, Menopur goes under the skin or into muscle. So this is read
   * off the packs still reachable from her choices: while they disagree she
   * is asked, and once they agree there is nothing to ask.
   */
  const reachableRoutes = useMemo(() => {
    const packs = matchedProduct?.packs ?? [];
    const chosen: Record<PackField, string> = {
      doseForm: value.doseForm,
      strength: value.strength,
      container: value.container,
      packQuantity: value.packQuantity,
    };
    const reachable = packs.filter((pack) =>
      PACK_FIELDS.every(
        (field) => !chosen[field] || pack[field] === chosen[field],
      ),
    );
    const source = reachable.length > 0 ? reachable : packs;
    return Array.from(
      new Set(source.flatMap((pack) => pack.routes ?? [])),
    ).filter((route): route is string => !!route);
  }, [
    matchedProduct,
    value.doseForm,
    value.strength,
    value.container,
    value.packQuantity,
  ]);

  const isAvailable = useCallback(
    (field: PackField, option: string) => {
      const packs = matchedProduct?.packs ?? [];
      if (packs.length === 0) return true;
      const chosen: Record<PackField, string> = {
        doseForm: value.doseForm,
        strength: value.strength,
        container: value.container,
        packQuantity: value.packQuantity,
      };
      const upstream = PACK_FIELDS.slice(0, PACK_FIELDS.indexOf(field));
      return packs.some(
        (pack) =>
          pack[field] === option &&
          upstream.every(
            (above) => !chosen[above] || pack[above] === chosen[above],
          ),
      );
    },
    [
      matchedProduct,
      value.doseForm,
      value.strength,
      value.container,
      value.packQuantity,
    ],
  );

  // A field with one reachable option is not a question. Choosing further up
  // often leaves the rest with a single answer, so this runs on what is
  // reachable now rather than once per product. It only ever writes an empty
  // field, so it settles rather than argues.
  useEffect(() => {
    const settled: Partial<MedicationIdentityDraft> = {};
    for (const field of PACK_FIELDS) {
      if (value[field].trim()) continue;
      const reachable = optionsFor(field).filter((option) =>
        isAvailable(field, option),
      );
      if (reachable.length === 1) settled[field] = reachable[0];
    }
    if (Object.keys(settled).length > 0) onChange({ ...value, ...settled });
  }, [optionsFor, isAvailable, onChange, value]);

  useEffect(() => {
    onRoutesChange?.(reachableRoutes);
  }, [onRoutesChange, reachableRoutes]);

  /**
   * Nothing left to choose: the catalogue holds one of each for this product,
   * so the four fields are a description rather than a form. They collapse to
   * one line — she can open it to check, but she is not being asked anything.
   */
  const settledPack = useMemo(
    () =>
      PACK_FIELDS.filter((field) => optionsFor(field).length === 1)
        .map((field) => value[field])
        .filter(Boolean),
    [optionsFor, value],
  );

  const packLeading = useMemo(() => {
    if (!renderPackLeading) return null;
    const doseForm = value.doseForm || optionsFor("doseForm")[0] || "";
    const container = value.container || optionsFor("container")[0] || "";
    if (!doseForm && !container) return null;
    return renderPackLeading({ doseForm, container });
  }, [renderPackLeading, optionsFor, value.doseForm, value.container]);

  const handleProductNameChange = useCallback(
    (nextName: string) => {
      // Naming a different medication clears everything that described the old
      // one's box — see withProductName.
      onChange(withProductName(value, nextName));
    },
    [onChange, value],
  );

  const handleSuggestionPress = useCallback(
    (product: MedicationCatalogProduct) => {
      onChange({
        ...withProductName(value, product.productName),
        brand: product.brand,
      });
    },
    [onChange, value],
  );

  return (
    <View testID="medication-identity-section">
      {filledFromScan ? (
        <View
          testID="medication-identity-scan-filled"
          className="flex-row items-center"
          style={{
            gap: 8,
            padding: 12,
            borderRadius: 10,
            marginBottom: 14,
            backgroundColor: semanticColors.accent.tint,
          }}
        >
          <Check size={14} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12, color: EMBER.colors.warm, flex: 1 }}
          >
            {t("add_medication.identity.filled_in_from_the_pack")}
          </Text>
        </View>
      ) : null}

      {/* The product is settled by now — by the search step, or by the pack she
          scanned. It is shown, not asked for again: a second search box here
          is the same question twice, and the one that could not see what the
          first one found. */}
      <MoonFormField
        label={t("add_medication.medication_name")}
        style={{ marginBottom: suggestions.length > 0 ? 6 : 16 }}
      >
        {product || productSelected ? (
          <MoonFieldInput paddingY={12}>
            <View
              className="flex-row items-center justify-between"
              style={{ gap: 10 }}
            >
              {/* Follows what she has chosen: the moment a dose form or
                  container settles, the picture is that pack's rather than
                  the product's first one. */}
              {packLeading ?? productLeading}
              <View
                className="flex-row items-center"
                style={{ flex: 1, gap: 8 }}
              >
                <Text
                  testID="medication-identity-product-name"
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.text,
                    flexShrink: 1,
                  }}
                >
                  {value.productName}
                </Text>
                {matchedProduct?.market ? (
                  <View testID="medication-identity-market">
                    <Tag label={matchedProduct.market} tone="sourceBlue" />
                  </View>
                ) : null}
              </View>
              {onChangeProduct ? (
                <TextActionButton
                  testID="medication-identity-change-product"
                  label={t("add_medication.change_medication")}
                  accessibilityLabel={t(
                    "add_medication.identity.pick_a_different_medication",
                  )}
                  action="medication_identity_change_product"
                  size="sm"
                  onPress={onChangeProduct}
                  style={{ paddingHorizontal: 0, paddingVertical: 0 }}
                />
              ) : null}
            </View>
          </MoonFieldInput>
        ) : (
          <MoonSearchInput
            testID="medication-identity-product-name"
            value={value.productName}
            onChangeText={handleProductNameChange}
            onClear={() => handleProductNameChange("")}
            loading={isSearchingProducts}
            placeholder={t("add_medication.identity.example_product_name")}
            accessibilityLabel={t("add_medication.medication_name")}
          />
        )}
      </MoonFormField>

      {!productSelected && suggestions.length > 0 ? (
        <View
          testID="medication-identity-product-suggestions"
          style={{ gap: 8, marginBottom: 16 }}
        >
          {suggestions.map((product) => (
            <Row
              key={product.medicationId}
              testID="medication-identity-product-suggestion"
              surface="input"
              title={product.productName}
              action="medication_product_suggestion"
              onPress={() => handleSuggestionPress(product)}
            />
          ))}
        </View>
      ) : null}

      {settledPack.length > 0 ? (
        <MoonFormField
          label={t("add_medication.identity.the_pack_you_have")}
          style={{ marginBottom: 16 }}
        >
          <Text
            testID="medication-identity-pack-summary"
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.text,
              paddingVertical: 2,
            }}
          >
            {settledPack
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(" · ")}
          </Text>
        </MoonFormField>
      ) : null}

      {/* Only what is still a question. Everything the catalogue settles is
          in the line above, named once rather than four times. */}
      <View>
        <IdentityOptionSection
          testID="medication-identity-dose-form"
          label={t("add_medication.identity.dose_form")}
          value={value.doseForm}
          options={optionsFor("doseForm")}
          onChange={(next) => choosePackField("doseForm", next)}
          isAvailable={(option) => isAvailable("doseForm", option)}
        />
        <IdentityOptionSection
          testID="medication-identity-strength"
          label={t("add_medication.identity.strength_on_the_pack")}
          value={value.strength}
          options={optionsFor("strength")}
          onChange={(next) => choosePackField("strength", next)}
          isAvailable={(option) => isAvailable("strength", option)}
        />
        <IdentityOptionSection
          testID="medication-identity-container"
          label={t("add_medication.identity.container")}
          value={value.container}
          options={optionsFor("container")}
          onChange={(next) => choosePackField("container", next)}
          isAvailable={(option) => isAvailable("container", option)}
        />
        <IdentityOptionSection
          testID="medication-identity-pack-size"
          label={t("add_medication.identity.how_much_is_in_the_pack")}
          value={value.packQuantity}
          options={optionsFor("packQuantity")}
          onChange={(next) => choosePackField("packQuantity", next)}
          isAvailable={(option) => isAvailable("packQuantity", option)}
        />
      </View>
    </View>
  );
}
